import { useEffect, useRef, useState } from "react";
import { fetchRunStatus, runNow } from "@/api/client";

export default function RunNowButton() {
  const [isRunning, setIsRunning] = useState(false);
  const [triggerSource, setTriggerSource] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const elapsedRef = useRef<number | null>(null);

  function stopPolling() {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  function startTicking(startedAt: number) {
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    const tick = () => {
      const seconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
      elapsedRef.current = seconds;
      setElapsedSeconds(seconds);
    };
    tick();
    tickRef.current = window.setInterval(tick, 1000);
  }

  function startPolling() {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const status = await fetchRunStatus();
        setIsRunning(status.isRunning);
        setTriggerSource(status.triggerSource);
        if (!status.isRunning) {
          setMessage(
            elapsedRef.current !== null ? `Cycle complete in ${elapsedRef.current}s.` : "Cycle complete.",
          );
          stopPolling();
          elapsedRef.current = null;
          setElapsedSeconds(null);
        }
      } catch {
      }
    }, 2500);
  }

  useEffect(() => {
    fetchRunStatus()
      .then((status) => {
        setIsRunning(status.isRunning);
        setTriggerSource(status.triggerSource);
        if (status.isRunning) {
          startPolling();
          startTicking(status.acquiredAt ?? Date.now());
        }
      })
      .catch(() => undefined);
    return stopPolling;
  }, []);

  async function handleClick() {
    setError(null);
    setMessage(null);
    try {
      const result = await runNow();
      if (!result.started) {
        setError(result.error ?? "Could not start cycle");
        return;
      }
      setIsRunning(true);
      setTriggerSource("manual");
      setMessage("Cycle started…");
      startPolling();
      startTicking(result.startedAt ?? Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start cycle");
    }
  }

  return (
    <div className="flex items-center gap-2">
      {(message || error) && (
        <span className={`font-mono text-[11px] ${error ? "text-sell" : "text-ink-faint"}`}>
          {error ?? message}
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={isRunning}
        title={
          isRunning
            ? `Running (${triggerSource ?? "unknown"})${elapsedSeconds !== null ? ` — ${elapsedSeconds}s` : ""}`
            : "Run a scan + trading cycle now"
        }
        className="inline-flex items-center gap-1.5 rounded-md border border-turquoise/50 bg-turquoise/10 px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-turquoise transition hover:bg-turquoise/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isRunning ? (
          <>
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-turquoise" />
            Running{elapsedSeconds !== null ? ` ${elapsedSeconds}s` : "…"}
          </>
        ) : (
          "Run now"
        )}
      </button>
    </div>
  );
}
